import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PortalIcon from './PortalIcon';
import PortalPaymentModal from './PortalPaymentModal';
import {
  externalTrackingUrl,
  formatDate,
  formatMoney,
  openAuthenticatedPdf,
  orderPaymentLabel,
  orderPaymentPending,
  orderProcessLabel,
  statusTone,
} from '../lib/portal';

function Badge({ tone, children }) {
  return <span className={`portal-badge portal-badge--${tone}`}>{children}</span>;
}

function readField(source, ...keys) {
  for (const key of keys) {
    if (source && source[key] != null && String(source[key]).trim()) return source[key];
  }
  return '—';
}

export default function PortalPedidoDetalle() {
  const { id } = useParams();
  const { authFetch, authRequestJson } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    authRequestJson(`/portal/pedidos/${id}`)
      .then((response) => { if (active) setPayload(response?.data || null); })
      .catch((requestError) => { if (active) setError(requestError.message || 'No se pudo cargar el pedido.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [authRequestJson, id]);

  const order = payload?.pedido || payload;
  const shippingAddress = payload?.shippingAddress && typeof payload.shippingAddress === 'object' ? payload.shippingAddress : {};
  const productsValue = payload?.products;
  const products = Array.isArray(productsValue) ? productsValue : Array.isArray(productsValue?.products) ? productsValue.products : [];
  const paymentBody = useMemo(() => ({ pedidoIds: [Number(id)] }), [id]);
  const trackingUrl = order?.trackingUrl || externalTrackingUrl(order?.trackingNumber);

  const printLabel = async () => {
    setPrinting(true);
    try {
      await openAuthenticatedPdf(authFetch, `/portal/pedidos/${id}/etiqueta.pdf`);
    } catch (requestError) {
      setError(requestError.message || 'No se pudo abrir el rótulo.');
    } finally {
      setPrinting(false);
    }
  };

  const cancelOrder = async () => {
    if (!window.confirm('¿Cancelar este pedido? Se anulará también en Tiendanube y no se podrá imprimir el rótulo.')) {
      return;
    }
    setCancelling(true);
    setError('');
    try {
      await authRequestJson(`/portal/pedidos/${id}/cancelar`, { method: 'POST' });
      window.location.reload();
    } catch (requestError) {
      setError(requestError.message || 'No se pudo cancelar el pedido.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="portal-app"><div className="portal-wrap portal-loading">Cargando el detalle…</div></div>;
  if (!order) return <div className="portal-app"><div className="portal-wrap"><div className="portal-error-panel"><strong>Pedido no encontrado.</strong>{error || 'El pedido ya no está disponible para esta cuenta.'}</div></div></div>;

  const process = orderProcessLabel(order);
  const pending = orderPaymentPending(order);
  const estado = (order.estadoInterno || '').toUpperCase();
  const shipping = (order.shippingStatus || '').toLowerCase();
  const isCancelled = estado === 'CANCELADO';
  const canCancel = !isCancelled && !estado.includes('DESPACHADO') && !estado.includes('ENTREGADO') && shipping !== 'shipped' && shipping !== 'delivered';

  return (
    <div className="portal-app">
      <div className="portal-wrap">
        <div className="portal-page-header">
          <Link className="portal-btn portal-btn--outline portal-btn--small" to="/pedidos"><PortalIcon name="back" size={15} /> Volver a pedidos</Link>
        </div>
        {error ? <div className="portal-alert portal-alert--error">{error}</div> : null}

        <div className="portal-detail-layout">
          <section className="portal-card portal-detail-main">
            <div className="portal-detail-topline">
              <div><p className="portal-kicker">Pedido Tiendanube</p><h2>#{order.tiendaNubeOrderNumber || order.tiendaNubeOrderId}</h2><p>{order.contactName || order.contactEmail || 'Cliente sin nombre'} · recibido {formatDate(order.actualizadoEn)}</p></div>
              <div className="portal-actions"><Badge tone={statusTone(process)}>{process}</Badge><Badge tone={statusTone(order.cobroEstado)}>{orderPaymentLabel(order)}</Badge></div>
            </div>

            <div className="portal-detail-grid">
              <div className="portal-detail-item"><span>Cliente</span><strong>{readField(order, 'contactName', 'contactEmail')}</strong></div>
              <div className="portal-detail-item"><span>Estado Tiendanube</span><strong>{readField(order, 'status', 'paymentStatus')}</strong></div>
              <div className="portal-detail-item"><span>Destino</span><strong>{readField(order, 'localidadDestino')}</strong></div>
              <div className="portal-detail-item"><span>Código postal</span><strong>{readField(order, 'codigoPostalDestino')}</strong></div>
              <div className="portal-detail-item"><span>Total de la orden</span><strong>{formatMoney(order.total)}</strong></div>
              <div className="portal-detail-item"><span>Flete ESA</span><strong>{pending ? formatMoney(order.valorViaje) : formatMoney(order.cobroMonto || order.valorViaje)}</strong></div>
            </div>

            {order.trackingNumber ? (
              <div className="portal-tracking-box"><strong>Seguimiento disponible</strong><span className="portal-muted">ID de seguimiento: {order.trackingNumber}</span><a href={trackingUrl} target="_blank" rel="noreferrer">Abrir seguimiento</a></div>
            ) : !pending ? (
              <div className="portal-tracking-box portal-tracking-box--info"><strong>Pedido abonado</strong><span className="portal-muted">El ID de seguimiento se generará al imprimir el rótulo.</span></div>
            ) : null}

            <div className="portal-form-section">
              <h3>Dirección de entrega</h3>
              <div className="portal-detail-grid">
                <div className="portal-detail-item"><span>Dirección</span><strong>{readField(shippingAddress, 'address', 'direccion', 'street')}</strong></div>
                <div className="portal-detail-item"><span>Localidad</span><strong>{readField(shippingAddress, 'city', 'localidad')}</strong></div>
                <div className="portal-detail-item"><span>Provincia</span><strong>{readField(shippingAddress, 'province', 'provincia', 'state')}</strong></div>
                <div className="portal-detail-item"><span>CP</span><strong>{readField(shippingAddress, 'zipcode', 'zip_code', 'codigoPostal')}</strong></div>
              </div>
            </div>

            <div className="portal-form-section">
              <h3>Productos</h3>
              {products.length === 0 ? <p className="portal-muted">La información de productos no está disponible para este pedido.</p> : (
                <div className="portal-table-wrap"><table className="portal-table"><thead><tr><th>Producto</th><th>Variante</th><th>Cantidad</th><th>Peso (kg)</th><th>Alto (cm)</th><th>Ancho (cm)</th><th>Largo (cm)</th></tr></thead><tbody>{products.map((product, index) => <tr key={product.id || index}><td><strong>{product.name || product.title || 'Producto'}</strong></td><td>{product.variant_name || product.variantName || product.variant || '—'}</td><td>{product.quantity || product.cantidad || 1}</td><td>{product.weight ? parseFloat(product.weight).toFixed(3) : '—'}</td><td>{product.height ? parseFloat(product.height).toFixed(2) : '—'}</td><td>{product.width ? parseFloat(product.width).toFixed(2) : '—'}</td><td>{product.depth ? parseFloat(product.depth).toFixed(2) : '—'}</td></tr>)}</tbody></table></div>
              )}
            </div>
          </section>

          <aside className="portal-card portal-detail-side">
            <div className="portal-side-stat"><span>Flete a abonar</span><strong>{pending ? formatMoney(order.valorViaje) : 'Abonado'}</strong></div>
            <div className="portal-side-stat"><span>Estado Saadis</span><strong>{order.saadisEstado || 'Pendiente'}</strong></div>
            {order.saadisComprobanteNumero ? <div className="portal-side-stat"><span>Comprobante Saadis</span><strong>{order.saadisComprobanteNumero}</strong></div> : null}
            <div className="portal-actions" style={{ marginTop: 24 }}>
              {pending ? <button type="button" className="portal-btn portal-btn--mint" onClick={() => setPayOpen(true)}><PortalIcon name="card" size={16} /> Pagar pedido</button> : null}
              <button
                type="button"
                className="portal-btn portal-btn--outline"
                disabled={printing || pending}
                title={pending ? 'Aboná el flete para imprimir el rótulo' : 'Imprimir rótulo'}
                onClick={() => void printLabel()}
              >
                <PortalIcon name="download" size={16} /> {printing ? 'Preparando…' : 'Imprimir rótulo'}
              </button>
              {canCancel ? (
                <button
                  type="button"
                  className="portal-btn portal-btn--danger"
                  disabled={cancelling}
                  onClick={() => void cancelOrder()}
                >
                  <PortalIcon name="close" size={16} /> {cancelling ? 'Cancelando…' : 'Cancelar pedido'}
                </button>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
      {payOpen ? (
        <PortalPaymentModal
          title={`Pagar pedido #${order.tiendaNubeOrderNumber || order.tiendaNubeOrderId}`}
          amount={order.valorViaje}
          preferencePath="/portal/pagos/preferencia"
          body={paymentBody}
          onClose={() => { setPayOpen(false); window.location.reload(); }}
        />
      ) : null}
    </div>
  );
}
